import { z } from "zod";
import { memberFullName } from "./format";
import type {
  ActivityParticipationCreateRequest,
  MemberResource,
  ParticipationResourceDetailed,
  ProfileResource,
  SitePullResource,
  SiteResource,
} from "@carnet/api/backoffice";

export type ActivityType = ActivityParticipationCreateRequest["activity"]["type"];
export type ActivityMode = ActivityParticipationCreateRequest["activity"]["mode"];

export interface ActivityCreateForm {
  type: ActivityType | "";
  mode: ActivityMode | "";
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  duration: number | null;
  maxDepth: number | null;
  siteId: number | null;
  // Nom de site libre saisi à l'étape site (texte de recherche réutilisé) : exclusif de `siteId`
  // (choisir un site répertorié le remet à null, et inversement). Voir getSiteDisplayName.
  customSiteName: string | null;
  siteClientUuid: string | null;
  structureId: number | null;
  // uuids v7 stables envoyés à la création (activité + participation) : rattachent les
  // photos (parentUuid = participation.clientUuid) et assurent l'idempotence `firstOrCreate`.
  activityClientUuid: string;
  participationClientUuid: string;
}

export const createActivityCreateForm = (): ActivityCreateForm => ({
  type: "",
  mode: "",
  date: "",
  time: "",
  duration: null,
  maxDepth: null,
  siteId: null,
  customSiteName: null,
  siteClientUuid: null,
  structureId: null,
  activityClientUuid: "",
  participationClientUuid: "",
});

// `t` minimal : suffisant pour les messages de validation (clé + interpolation éventuelle).
type TranslateFn = (key: string, named?: Record<string, unknown>) => string;

export const activityTypeStepSchema = (t: TranslateFn) =>
  z.object({
    type: z.string().min(1, t("form.validation.select_option")),
    mode: z.string().min(1, t("form.validation.select_option")),
  });

export const activityDatetimeStepSchema = (t: TranslateFn) =>
  z.object({
    date: z.string().min(1, t("form.validation.required")),
    time: z.string().min(1, t("form.validation.required")),
  });

export const activityDepthStepSchema = (t: TranslateFn) =>
  z.object({
    duration: z
      .number({ message: t("form.validation.duration_required") })
      .int(t("form.validation.duration_range"))
      .min(1, t("form.validation.duration_positive"))
      .max(86400, t("form.validation.duration_range")),
    maxDepth: z
      .number({ message: t("form.validation.maxDepth_required") })
      .min(1, t("form.validation.maxDepth_positive"))
      .max(10984, t("form.validation.maxDepth_range"))
      .multipleOf(0.1, t("form.validation.maxDepth_decimals")),
  });

// Récapitulatif : tous les champs de saisie réunis (le site est validé à son étape).
export const activitySummarySchema = (t: TranslateFn) =>
  z.object({
    ...activityTypeStepSchema(t).shape,
    ...activityDatetimeStepSchema(t).shape,
    ...activityDepthStepSchema(t).shape,
  });

export function siteReferencePayload(
  siteId: number | null | undefined,
  siteClientUuid: string | null | undefined
): Pick<ActivityParticipationCreateRequest["activity"], "siteId" | "siteClientUuid"> {
  const serverSiteId = siteId != null && siteId > 0 ? siteId : undefined;
  return { siteId: serverSiteId, siteClientUuid: serverSiteId == null ? (siteClientUuid ?? undefined) : undefined };
}

type CodeLabel = { code: string; label: string };

export interface ActivityCreateReferentials {
  activityTypes?: ReadonlyArray<CodeLabel>;
  activityModes?: ReadonlyArray<CodeLabel>;
  site?: SiteResource | null;
  creatorMember: MemberResource;
}

// `participationCount` à 0 : agrégat non porté par le feed offline `sites/pull` (SitePullResource).
export function siteResourceFromPull(site: SitePullResource | null | undefined): SiteResource | null {
  if (!site) return null;
  return {
    id: site.id,
    clientUuid: site.clientUuid,
    name: site.name,
    searchableName: site.searchableName,
    validationStatus: site.validationStatus,
    zone: site.zone,
    latitude: site.latitude,
    longitude: site.longitude,
    nearbyCityLabel: site.nearbyCityLabel,
    participationCount: 0,
    activityTypes: site.activityTypes,
    depthCategory: site.depthCategory,
    environment: site.environment,
  };
}

export function memberResourceFromProfile(profile: ProfileResource): MemberResource {
  return {
    id: profile.id,
    invitationEmail: profile.invitationEmail,
    lastName: profile.lastName,
    firstName: profile.firstName,
    fullName: memberFullName(profile),
    gender: profile.gender,
    isSupervisor: profile.isSupervisor,
    clubName: profile.clubName,
    licenceCode: profile.licenceCode,
    licenceType: profile.licenceType,
    photoUrl: profile.photoUrl,
    isNaturalist: profile.isNaturalist,
    deletedProfile: false,
    initialParticipationsCount: profile.initialParticipationsCount,
  };
}

// `id` à 0 : placeholder écrasé par l'id négatif (pending) qu'assigne `addPendingParticipation`.
export function buildPendingParticipationResource(
  body: ActivityParticipationCreateRequest,
  referentials: ActivityCreateReferentials
): ParticipationResourceDetailed {
  const labeled = (list: ReadonlyArray<CodeLabel> | undefined, code: string): CodeLabel => ({
    code,
    label: list?.find((item) => item.code === code)?.label ?? code,
  });
  const activity = body.activity;
  return {
    id: 0,
    clientUuid: body.participation?.clientUuid ?? "",
    invitationStatus: "accepted",
    validationStatus: "none",
    activity: {
      id: 0,
      clientUuid: activity.clientUuid ?? null,
      hasAcceptedParticipations: true,
      hasValidatedParticipations: false,
      type: { code: activity.type, label: labeled(referentials.activityTypes, activity.type).label },
      mode: labeled(referentials.activityModes, activity.mode),
      maxDepth: activity.maxDepth,
      endedAt: activity.endedAt,
      duration: activity.duration,
      site: referentials.site ?? null,
      customSiteName: activity.customSiteName ?? null,
      creatorMember: referentials.creatorMember,
      supervisorMember: null,
      participations: [{ id: 0, clientUuid: null, activityId: 0, member: referentials.creatorMember, invitationStatus: "accepted" }],
      structure: null,
      temperatureOutside: null,
      temperatureSurface: null,
      temperatureBottom: null,
      dayPeriod: null,
      seaAgitation: null,
      weather: null,
      visibility: null,
      seaStream: null,
    },
    participationCounter: 0,
    validationMessage: "",
    validatorMember: null,
    ballast: null,
    suitThickness: null,
    equipmentComment: "",
    globalComment: "",
    stages: null,
    gases: null,
    immersions: null,
    evaluation: null,
    wetSuit: null,
    scubaTank: null,
    photos: [],
    updatedAt: new Date().toISOString(),
    deletedAt: null,
  };
}

// Construit le corps POST `activityParticipations/create` à partir du formulaire. Les
// numériques sont validés ≥ 1 à l'étape profondeur ; `?? 0` est un filet défensif.
export function buildActivityCreateBody(form: ActivityCreateForm): ActivityParticipationCreateRequest {
  return {
    activity: {
      clientUuid: form.activityClientUuid,
      type: form.type as ActivityType,
      mode: form.mode as ActivityMode,
      endedAt: `${form.date} ${form.time}:00`,
      maxDepth: form.maxDepth ?? 0,
      duration: form.duration ?? 0,
      ...siteReferencePayload(form.siteId, form.siteClientUuid),
      customSiteName: form.customSiteName ?? undefined,
      structureId: form.structureId ?? undefined,
    },
    participation: {
      clientUuid: form.participationClientUuid,
    },
  };
}
